/**
 * Japanese national holidays service + React hook.
 *
 * Data source: holidays table via API /api/v1/holidays.
 * Fetched once and cached in memory.
 */

import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/shared/lib/axios';

/** RA2NDatePicker holiday format: { "YYYY-MM": ["01","15",...] } */
export type HolidaysMap = Record<string, string[]>;

/** API response format: { "YYYY-MM-DD": "holiday name" } */
type HolidaysRaw = Record<string, string>;

/**
 * Convert API format `{ "2026-01-01": "元日" }` → HolidaysMap `{ "2026-01": ["01"] }`.
 */
export function toHolidaysMap(apiData: HolidaysRaw): HolidaysMap {
  const map: HolidaysMap = {};
  for (const dateStr of Object.keys(apiData)) {
    const [y, m, d] = dateStr.split('-');
    if (!y || !m || !d) continue;
    const key = `${y}-${m}`;
    if (!map[key]) map[key] = [];
    map[key].push(d);
  }
  return map;
}

/** Module-level cache so we only fetch once across all components. */
let cachedRaw: HolidaysRaw | null = null;
let cachedMap: HolidaysMap | null = null;
let fetchPromise: Promise<void> | null = null;

/** Flat date set for quick lookup (e.g. calendar header). */
let cachedDateSet: Set<string> | null = null;

async function ensureLoaded(): Promise<void> {
  if (cachedRaw) return;
  if (fetchPromise) return fetchPromise;

  fetchPromise = apiClient
    .get<{ success: boolean; data: HolidaysRaw }>('/holidays')
    .then(res => {
      cachedRaw = res.data?.data ?? {};
      cachedMap = toHolidaysMap(cachedRaw);
      cachedDateSet = new Set(Object.keys(cachedRaw));
    })
    .catch(() => {
      // Fallback to empty on error
      cachedRaw = {};
      cachedMap = {};
      cachedDateSet = new Set();
    });

  return fetchPromise;
}

/**
 * React hook: returns Japanese holidays as HolidaysMap compatible with RA2NDatePicker's `holidays` prop.
 */
export function useJpHolidays(_viewYear?: number): HolidaysMap {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (cachedMap) return;
    ensureLoaded().then(() => setTick(t => t + 1));
  }, []);

  return useMemo(() => cachedMap ?? {}, [cachedMap]);
}

/**
 * React hook: returns a Set of holiday date strings (YYYY-MM-DD) for quick lookup.
 */
export function useJpHolidayDates(): Set<string> {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (cachedDateSet) return;
    ensureLoaded().then(() => setTick(t => t + 1));
  }, []);

  return useMemo(() => cachedDateSet ?? new Set<string>(), [cachedDateSet]);
}
